/**
 * Selectable behavior wrapper for editor mode
 *
 * Wraps a visual component and adds click-to-select behavior.
 * Uses the BlockRenderContext's selectedIds and onSelect callback.
 */

import React from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import type { BlockBehaviorProps } from '../../Types'
import { useSelect } from './UseSelect'

interface SelectableProps extends BlockBehaviorProps {
  blockId: string
  children: React.ReactElement
}

/**
 * Makes a block selectable in editor mode
 *
 * Click to select a single block
 * Shift+click to toggle selection (additive selection)
 *
 * Wraps children in a div with selection behavior rather than cloning,
 * to work properly with other wrapper components like Draggable.
 *
 * @example
 * ```tsx
 * <Selectable blockId={block.id}>
 *   <PlayerVisual block={playerBlock} />
 * </Selectable>
 * ```
 */
export function Selectable({ blockId, children }: SelectableProps) {
  const context = useBlockRenderContext()

  // Call hooks unconditionally (React rules of hooks)
  const isEditorMode = context.mode === 'editor' && !!context.onSelect && !!context.selectedIds
  const { isSelected, handlers } = useSelect({
    blockId,
    selectedIds: context.selectedIds ?? new Set(),
    onSelect: context.onSelect ?? (() => {}),
    enabled: isEditorMode,
  })

  // Only enable selection in editor mode - return early AFTER hooks
  if (!isEditorMode) {
    return children
  }

  // Wrap in a div with selection styles
  return (
    <div
      onClick={handlers.onClick}
      style={{
        boxShadow: isSelected ? '0 0 0 3px var(--ring)' : undefined,
        cursor: 'pointer',
        pointerEvents: 'auto',
      }}
    >
      {children}
    </div>
  )
}
